import React, { useState, useMemo } from "react";
import { useLang } from "../lib/LanguageContext";
import PageHeader from "../components/PageHeader";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { X, ZoomIn } from "lucide-react";

// Build image lists from /public/gallery/<folder>/img-XX.jpg
const buildList = (folder, count) =>
  Array.from({ length: count }, (_, i) => `/gallery/${folder}/img-${String(i + 1).padStart(2, "0")}.jpg`);

const IMAGES = {
  purv: buildList("purv", 30),
  prathmik: buildList("prathmik", 9),
  madhyamik: buildList("madhyamik", 22),
};

const Gallery = () => {
  const { t } = useLang();
  const p = t.galleryPg;
  const [active, setActive] = useState("all");
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ src: "", caption: "" });

  const items = useMemo(() => {
    const list = [];
    p.categories.forEach((c) => {
      const imgs = IMAGES[c.key] || [];
      imgs.forEach((src, idx) => {
        list.push({ src, key: `${c.key}-${idx}`, catKey: c.key, catTitle: c.title, caption: c.caption });
      });
    });
    return active === "all" ? list : list.filter((i) => i.catKey === active);
  }, [active, p.categories]);

  const openLightbox = (src, caption) => {
    setLightbox({ src, caption });
    setOpen(true);
  };

  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow={t.nav.gallery} title={p.title} subtitle={p.sub} testId="gallery-header" />

      {/* Filter tabs */}
      <section className="py-8 md:py-10">
        <div className="container-school">
          <div className="flex flex-wrap gap-2 md:gap-3" data-testid="gallery-filters">
            <button
              onClick={() => setActive("all")}
              data-testid="filter-all"
              className={`whitespace-nowrap px-6 py-3 rounded-full font-heading font-semibold text-sm md:text-base transition-all ${
                active === "all"
                  ? "bg-school-primary text-white shadow-md"
                  : "bg-white border border-school text-school-text hover:border-school-primary hover:text-school-primary"
              }`}
            >
              {p.filterAll}
            </button>
            {p.categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                data-testid={`filter-${c.key}`}
                className={`whitespace-nowrap px-6 py-3 rounded-full font-heading font-semibold text-sm md:text-base transition-all ${
                  active === c.key
                    ? "bg-school-primary text-white shadow-md"
                    : "bg-white border border-school text-school-text hover:border-school-primary hover:text-school-primary"
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active category header — left aligned consistent with other pages */}
      {active !== "all" && (
        <section className="pb-4">
          <div className="container-school">
            <div className="max-w-3xl">
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-school-primary">
                {p.categories.find((c) => c.key === active)?.title}
              </h2>
              <p className="mt-3 text-base md:text-lg text-school-muted">
                {p.categories.find((c) => c.key === active)?.caption}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-20">
        <div className="container-school">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {items.map((it, i) => (
              <button
                key={it.key}
                onClick={() => openLightbox(it.src, `${it.catTitle} — ${it.caption}`)}
                data-testid={`gallery-item-${i}`}
                className="group relative rounded-2xl overflow-hidden bg-school-muted aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-school-primary focus:ring-offset-2"
              >
                <img
                  src={it.src}
                  alt={it.catTitle}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-school-primary/80 via-school-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-3 left-3 right-3 text-left translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="font-heading font-bold text-white text-sm leading-tight">{it.catTitle}</div>
                </div>
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn className="w-4 h-4 text-school-primary" />
                </div>
              </button>
            ))}
          </div>
          {items.length === 0 && (
            <div className="text-center py-20 text-school-muted">No images</div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="lightbox-content"
          className="max-w-5xl w-[95vw] p-0 border-0 bg-transparent shadow-none"
        >
          <DialogTitle className="sr-only">{lightbox.caption}</DialogTitle>
          <div className="relative bg-black rounded-2xl overflow-hidden">
            <button
              onClick={() => setOpen(false)}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-school-text" />
            </button>
            <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[80vh] object-contain" />
            {lightbox.caption && (
              <div className="px-6 py-4 bg-school-primary text-white text-sm md:text-base font-heading">
                {lightbox.caption}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
